import { SyntheticEvent, useEffect, useState } from 'react';
import profileImage from '@assets/images/profile.png';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';
import { getDayBounds } from './dateHandler';

export enum RoleType {
	BDE = 'BDE',
	KAENC = 'KAENC',
	KAERR = 'KAERR',
	TSE = 'TSE',
	TSI = 'TSI',
	IMR = 'IMR',
	KAE = 'KAE',
	KAESS = 'KAESS',
	RBDM = 'RBDM',
	SS = 'SS',
	BDESE = 'BDE SE',
}

export const convertPathNameToTitle = (title: string): string => {
	const pathSegments = title.split('/').filter((segment) => segment !== '');

	const lastSegment = pathSegments[pathSegments.length - 1];
	return lastSegment
		?.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

export const onProfileImageError = (e: SyntheticEvent<HTMLImageElement, Event>): void => {
	e.currentTarget.src = profileImage;
};

export const keyDownEvent = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
	const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace', 'Delete', 'Home', 'Tab', 'End'];
	const key = e.key;

	if (/^\d$/.test(key)) return true;

	if (allowedKeys.includes(key)) return true;

	if (e.ctrlKey && (key === 'c' || key === 'v' || key === 'a' || key === 'x' || key === 'z')) return true;

	e.preventDefault();
	return false;
};

export const escapeRegExp = (string: string): string => {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const handlePreventDefault = <T extends { preventDefault: () => void }>(e: T): void => {
	e.preventDefault();
};

export const handleStopPropagation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
	e.stopPropagation();
};

export const onDownload = (file: string | undefined): void => {
	if (!file) {
		return;
	}
	const currentTimestamp = new Date().getTime();
	const fileUrl = file;
	const downloadLink = document.createElement('a');
	downloadLink.href = fileUrl;
	downloadLink.download = currentTimestamp.toString();
	// Open the file in a new tab
	downloadLink.target = '_blank';
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
};

export const setRxdb = async <T,>(name: string, response: Array<T>): Promise<void> => {
	const db = await getDatabase();
	await getDatabase();
	const dbData = await db[name].find().exec();
	const primaryId = dbData.map((doc: { primary: string }) => doc.primary);
	await db[name].bulkRemove(primaryId);
	await db[name].bulkInsert(response);
};

export interface DataRow {
	[key: string]: string | number | boolean;
}

export const compareDatePartsOnly = (date1: Date, date2: Date): boolean => {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
};
// one to one meeting date status
export type MeetingDateStatus = 'past' | 'today' | 'future' | 'unknown';

export const getMeetingDateStatus = (input: Date | string | null | undefined): MeetingDateStatus => {
	if (!input) return 'unknown';
	const parsed = input instanceof Date ? input : new Date(input);
	if (Number.isNaN(parsed.getTime())) return 'unknown';
	const today = new Date();
	if (compareDatePartsOnly(parsed, today)) return 'today';
	const { startDate } = getDayBounds(today);
	return parsed < startDate ? 'past' : 'future';
};

// decimal number with two decimals
export const formatNumberWithTwoDecimals = (value: number): string => {
	if (Number.isInteger(value)) {
		return value.toLocaleString();
	}
	return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const getImageWithSas = async (url: string): Promise<string> => {
	const SAS_TOKEN_REGEX = /[?&]se=/;

	if (!SAS_TOKEN_REGEX.test(url)) return url;
	const response = await APIService.postData<{
		normal_url: string | PromiseLike<string>;
		url: string;
	}>(`${APICONFIG.IMAGE_FETCH_API}`, { url }, { 'Content-Type': 'application/json' });
	return response?.normal_url;
};

export const extractFileNameFromUrl = (url: string): string => {
	try {
		const decodedUrl = decodeURIComponent(url);
		const regex = /-name-(.+?)(\?|$)/;
		const match = regex.exec(decodedUrl);
		return match?.[1] ?? '';
	} catch {
		return '';
	}
};

export const deg2rad = (deg: number): number => deg * (Math.PI / 180);

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
	const R = 6371; // Radius of the Earth in kilometers
	const dLat = deg2rad(lat2 - lat1);
	const dLon = deg2rad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

export const getCurrentLocation = (): Promise<{ lat: number; lng: number }> => {
	return new Promise((resolve) => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					resolve({
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					});
				},
				() => {
					resolve({ lat: 23.0513141, lng: 72.6243325 });
				}
			);
		} else {
			resolve({ lat: 23.0513141, lng: 72.6243325 });
		}
	});
};

/** Resolves only with the device position; rejects if permission is denied or geolocation fails. */
export const getRequiredCurrentLocation = (): Promise<{ lat: number; lng: number }> => {
	return new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			reject(new Error('Geolocation is not supported by your browser.'));
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				});
			},
			(error) => {
				reject(new Error(error.message || 'Unable to get current location.'));
			},
			{
				enableHighAccuracy: true,
				timeout: 15000,
				maximumAge: 0,
			}
		);
	});
};

export const toStr = (v: string | number | null | undefined): string => (v != null && v !== '' ? String(v) : '');

export const arrToStr = (v: string[] | null | undefined): string =>
	Array.isArray(v) && v.length > 0 ? v.join(',') : '';

export const strToArr = (v: string | null | undefined): string[] => (v != null && v !== '' ? v.split(',') : []);

export const formatApiMessage = (message?: string): string => {
	if (!message) return '-';
	const cleaned = message
		.replace(/<br\s*\/?>/gi, ' ')
		.replace(/<\/?[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	const msgMatch = cleaned.match(/MESSAGE:\s*(.*?)\s*(STATUS:|ERROR:|IS_INSTANTREWARDS_USER:|$)/i);
	if (msgMatch?.[1]) return msgMatch[1].trim();
	return cleaned.replace(/^Error:\s*/i, '').trim() || '-';
};

export const parseApiErrorDetails = (message?: string): Record<string, string> => {
	if (!message) return {};
	const cleaned = message.replace(/^Error:\s*/i, '');
	return cleaned.split(/<br\s*\/?>/i).reduce<Record<string, string>>((acc, segment) => {
		const trimmed = segment.replace(/<\/?[^>]+>/g, '').trim();
		if (!trimmed) return acc;
		const [rawKey, ...rest] = trimmed.split(':');
		if (!rawKey || rest.length === 0) return acc;
		const key = rawKey.trim().toLowerCase();
		const value = rest.join(':').trim();
		if (key) acc[key] = value;
		return acc;
	}, {});
};

export const formatApiStatus = (message?: string): boolean => {
	if (!message) return false;
	const cleaned = message
		.replace(/<br\s*\/?>/gi, ' ')
		.replace(/<\/?[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	const msgMatch = cleaned.match(/STATUS:\s*(.*?)$/i);
	if (!msgMatch) return false;
	return msgMatch[1].trim().toLowerCase() === 'false';
};

/**
 * Normalizes a value that may be a string array, comma-separated string, or null/undefined into string[] | null.
 * - Array: returned as-is
 * - String: split by comma, trim each part, filter empty
 * - null/undefined: returns null
 */
export const toStringArrayOrNull = (v: string[] | string | null | undefined): string[] | null => {
	if (v == null) return null;
	if (Array.isArray(v)) return v;
	if (typeof v === 'string') {
		return v
			.split(',')
			.map((s: string) => s.trim())
			.filter(Boolean);
	}
	return null;
};

/** Progress animation while a verification/submit request is pending (bank KYC, selfie verify, etc.). */
export const VERIFICATION_SUBMIT_PROGRESS = {
	START: 10,
	END: 100,
	DURATION_MS: 2500,
	TICK_MS: 80,
} as const;

/**
 * While `isSubmitting` is true, animates progress from START toward END. When not submitting, sets progress to END if
 * `status` is not `verifying`, otherwise START (same behavior as the former inline effects in AccountDetails / VarifySelfie).
 */
export function useVerificationSubmitProgress(isSubmitting: boolean, status: string): number {
	const { START, END, DURATION_MS, TICK_MS } = VERIFICATION_SUBMIT_PROGRESS;
	const [progress, setProgress] = useState(START);

	useEffect(() => {
		if (!isSubmitting) {
			setProgress(status !== 'verifying' ? END : START);
			return;
		}
		setProgress(START);
		const startTime = Date.now();
		const intervalId = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const value = START + (elapsed / DURATION_MS) * (END - START);
			setProgress(Math.min(END, value));
		}, TICK_MS);
		return (): void => clearInterval(intervalId);
	}, [isSubmitting, status, START, END, DURATION_MS, TICK_MS]);

	return progress;
}

export const NUMERIC_INPUT_PROPS = {
	inputMode: 'numeric' as const,
	min: 0,
	step: 1,
	maxLength: 10,
	pattern: '[0-9]*',
	onInput: (e: React.FormEvent<HTMLInputElement>): void => {
		(e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
	},
};

export const FLOAT_INPUT_PROPS = {
	inputMode: 'decimal' as const, // better for mobile keyboards
	min: 0,
	step: 'any', // allows decimals
	maxLength: 10,
	pattern: '[0-9]*[.]?[0-9]*',
	onInput: (e: React.FormEvent<HTMLInputElement>): void => {
		const input = e.target as HTMLInputElement;

		// Allow only digits and one decimal point
		let value = input.value.replace(/[^0-9.]/g, '');

		// Ensure only one dot
		const parts = value.split('.');
		if (parts.length > 2) {
			value = parts[0] + '.' + parts.slice(1).join('');
		}

		if (parts[1]) {
			value = `${parts[0]}.${parts[1].slice(0, 3)}`;
		}

		input.value = value;
	},
};

export function parseQueryId(id: number | string | null | undefined): number | null {
	if (id === null || id === undefined || id === '') return null;
	const n = Number(id);
	return Number.isFinite(n) && n > 0 ? n : null;
}

export function parseSurfaceId(id: string | null | undefined): string | null {
	if (id === null || id === undefined || id === '') return null;
	return id;
}

export function handleMeetingClick(status: string): string {
	switch (status) {
		case 'Scheduled Meeting':
			return '/meeting/meeting-details';
		case 'MAR(Today Meeting)':
			return '/today-meetings';
		case 'List Of Meeting Conducted':
			return '/today-meetings';
		case 'Completed Meeting':
			return '/complete-meeting-details';
		default:
			return '/';
	}
}

export const getInitials = (name: string): string => {
	if (!name) return '';
	const parts = name.trim().split(/\s+/);

	if (parts.length === 1) {
		return parts[0][0].toUpperCase();
	}

	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Coerce expense form values (number or in-progress string) for Yup; invalid → NaN. */
export const expenseFormAmountToNumber = (originalValue: unknown): number | null => {
	if (originalValue === null || originalValue === undefined || originalValue === '') return null;
	if (typeof originalValue === 'number') return Number.isFinite(originalValue) ? originalValue : null;
	const s = String(originalValue).trim();
	if (s === '.') return null;
	const n = parseFloat(s);
	return Number.isFinite(n) ? n : null;
};

export const formatIndianNumber = (num: number): string => {
	if (num >= 1000000000000) return (num / 1000000000000).toFixed(2) + ' L Cr'; // ≥ 1 lakh crore (10^12)
	if (num >= 10000000) return (num / 10000000).toFixed(2) + ' Cr'; // ≥ 1 crore (10^7)
	if (num >= 100000) return (num / 100000).toFixed(1) + ' L'; // ≥ 1 lakh (10^5)
	if (num >= 1000) return (num / 1000).toFixed(1) + ' K'; // ≥ 1 thousand (10^3)
	return num.toString();
};

//DropDown caching start here
/** Max age of last remote fetch (`dropdowns.expiry_date`) before RxDB-backed queries refetch from the API. */
//export const REMOTE_RX_QUERY_CACHE_TTL_MS = 30 * 1000; // 30 seconds

export const REMOTE_RX_QUERY_CACHE_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

/**
 * Logical cache keys (unchanged for callers). Values are stored in RxDB `dropdowns.expiry_date`, not cookies.
 */
export const REMOTE_RX_CACHE_KEY_END_USER_CATEGORY = 'end_user_category_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_DEALERS = 'dealers_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_PRODUCTS = 'products_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_TOWNS = 'towns_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_VENUES = 'venues_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_SURFACES = 'surfaces_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_TYPE_OF_CONSTRUCTION = 'type_of_construction_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_PROJECT_CLASSIFICATION = 'project_classification_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_STAGE_OF_CONSTRUCTION = 'stage_of_construction_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_SOURCE_OF_SITE_LEAD = 'source_of_site_lead_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_MEETING_MODULES = 'meeting_modules_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_MEETING_TYPES = 'meeting_types_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_BTL_OBJECTIVE = 'btl_objective_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_PRODUCT_MASTER = 'product_master_last_fetch_at';
export const REMOTE_RX_CACHE_KEY_CC_TOWN_LIST = 'cc_town_list_last_fetch_at';

const REMOTE_RX_CACHE_KEY_TO_DROPDOWN: Record<string, { id: string; name: string }> = {
	[REMOTE_RX_CACHE_KEY_END_USER_CATEGORY]: { id: '1', name: 'end_user_categories' },
	[REMOTE_RX_CACHE_KEY_DEALERS]: { id: '2', name: 'dealer_list' },
	[REMOTE_RX_CACHE_KEY_PRODUCTS]: { id: '3', name: 'product_list' },
	[REMOTE_RX_CACHE_KEY_TOWNS]: { id: '4', name: 'town_list' },
	[REMOTE_RX_CACHE_KEY_VENUES]: { id: '5', name: 'venue_list' },
	[REMOTE_RX_CACHE_KEY_SURFACES]: { id: '6', name: 'surface_list' },
	[REMOTE_RX_CACHE_KEY_TYPE_OF_CONSTRUCTION]: { id: '7', name: 'type_of_construction_list' },
	[REMOTE_RX_CACHE_KEY_PROJECT_CLASSIFICATION]: { id: '8', name: 'project_classification_list' },
	[REMOTE_RX_CACHE_KEY_STAGE_OF_CONSTRUCTION]: { id: '9', name: 'stage_of_construction_list' },
	[REMOTE_RX_CACHE_KEY_SOURCE_OF_SITE_LEAD]: { id: '10', name: 'source_of_site_lead_list' },
	[REMOTE_RX_CACHE_KEY_MEETING_MODULES]: { id: '11', name: 'module_name_list' },
	[REMOTE_RX_CACHE_KEY_MEETING_TYPES]: { id: '12', name: 'meeting_type_list' },
	[REMOTE_RX_CACHE_KEY_BTL_OBJECTIVE]: { id: '13', name: 'btl_objective_list' },
	[REMOTE_RX_CACHE_KEY_PRODUCT_MASTER]: { id: '14', name: 'product_master_list' },
	[REMOTE_RX_CACHE_KEY_CC_TOWN_LIST]: { id: '15', name: 'cc_town_list' },
};

const padRemoteRxDatePart = (n: number): string => String(n).padStart(2, '0');

/** Stores last sync time as `DD/MM/YYYY HH:MM:SS` (local) in `dropdowns.expiry_date`. */
export const formatRemoteRxCacheCookieDate = (epochMs: number): string => {
	const d = new Date(epochMs);
	return `${padRemoteRxDatePart(d.getDate())}/${padRemoteRxDatePart(d.getMonth() + 1)}/${d.getFullYear()} ${padRemoteRxDatePart(d.getHours())}:${padRemoteRxDatePart(d.getMinutes())}:${padRemoteRxDatePart(d.getSeconds())}`;
};

/** Parses stored value to epoch ms; supports `DD/MM/YYYY HH:MM:SS` and legacy plain epoch ms strings. */
export const parseRemoteRxCacheCookieDate = (raw: string): number => {
	const trimmed = raw.trim();
	if (trimmed === '') {
		return 0;
	}
	if (/^\d{10,}$/.test(trimmed)) {
		const n = Number(trimmed);
		return Number.isFinite(n) ? n : 0;
	}
	const m = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
	if (!m) {
		return 0;
	}
	const day = Number.parseInt(m[1], 10);
	const month = Number.parseInt(m[2], 10) - 1;
	const year = Number.parseInt(m[3], 10);
	const hour = Number.parseInt(m[4], 10);
	const minute = Number.parseInt(m[5], 10);
	const second = Number.parseInt(m[6], 10);
	const d = new Date(year, month, day, hour, minute, second);
	const t = d.getTime();
	return Number.isNaN(t) ? 0 : t;
};

const readLegacyRemoteRxCacheCookie = (name: string): string | null => {
	if (typeof document === 'undefined') {
		return null;
	}
	const encodedName = `${encodeURIComponent(name)}=`;
	const segments = document.cookie.split('; ');
	for (const segment of segments) {
		if (segment.startsWith(encodedName)) {
			return decodeURIComponent(segment.slice(encodedName.length));
		}
	}
	return null;
};

const clearLegacyRemoteRxCacheCookie = (name: string): void => {
	if (typeof document === 'undefined') {
		return;
	}
	document.cookie = `${encodeURIComponent(name)}=;path=/;max-age=0`;
};

/**
 * Last remote fetch time for a synced list, stored on the matching `dropdowns` row (`expiry_date`).
 * `cacheKey` — a `REMOTE_RX_CACHE_KEY_*` constant that has a matching row in the dropdowns collection.
 */
export const readRemoteRxCacheTimestamp = async (cacheKey: string): Promise<number> => {
	if (typeof window === 'undefined') {
		return 0;
	}
	const meta = REMOTE_RX_CACHE_KEY_TO_DROPDOWN[cacheKey];
	if (!meta) {
		return 0;
	}
	try {
		const db = await getDatabase();
		const doc = await db.dropdowns.findOne(meta.id).exec();
		const raw = (doc?.toJSON?.() as { expiry_date?: string } | undefined)?.expiry_date?.trim() ?? '';
		let ts = raw ? parseRemoteRxCacheCookieDate(raw) : 0;
		if (ts === 0) {
			const legacy = readLegacyRemoteRxCacheCookie(cacheKey);
			if (legacy) {
				ts = parseRemoteRxCacheCookieDate(legacy);
				if (ts > 0) {
					await writeRemoteRxCacheTimestamp(cacheKey, ts);
					clearLegacyRemoteRxCacheCookie(cacheKey);
				}
			}
		}
		return ts;
	} catch {
		return 0;
	}
};

export const writeRemoteRxCacheTimestamp = async (cacheKey: string, ts: number): Promise<void> => {
	if (typeof window === 'undefined') {
		return;
	}
	const meta = REMOTE_RX_CACHE_KEY_TO_DROPDOWN[cacheKey];
	if (!meta) {
		return;
	}
	try {
		const db = await getDatabase();
		const formatted = formatRemoteRxCacheCookieDate(ts);
		const existing = await db.dropdowns.findOne(meta.id).exec();
		if (existing) {
			await existing.patch({ expiry_date: formatted });
		} else {
			await db.dropdowns.insert({
				id: meta.id,
				name: meta.name,
				expiry_date: formatted,
			});
		}
		clearLegacyRemoteRxCacheCookie(cacheKey);
	} catch {
		/* ignore */
	}
};

/** True when no timestamp is stored yet, or the last remote sync is older than `maxAgeMs`. */
export const isRemoteRxCacheExpired = async (cacheKey: string, maxAgeMs: number): Promise<boolean> => {
	const last = await readRemoteRxCacheTimestamp(cacheKey);
	if (last === 0) {
		return true;
	}
	return Date.now() - last >= maxAgeMs;
};

//DropDown caching end here
